/**
 * MediaEditor Constants
 * Platform configurations, aspect ratios, and default settings
 * 
 * FIXED: LinkedIn aspect ratio configuratie (1.91:1 standaard, 1:1 alternatief)
 */

import { PlatformConfig, AspectRatioOption } from './types';

// Platform configurations with aspect ratios
// NOTE: LinkedIn uses 1.91:1 for landscape posts and 1:1 for square posts
export const PLATFORM_CONFIGS: PlatformConfig[] = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: '💼',
    aspectRatio: '1.91:1', // Landscape format - most common for LinkedIn
    dimensions: { width: 1200, height: 628 }, // Corrected: 1200/628 ≈ 1.91
    alternativeAspects: ['1.91:1', '1:1'] // Landscape and square options
  },
  {
    id: 'x',
    name: 'Twitter/X',
    icon: '🐦',
    aspectRatio: '16:9', // Standard Twitter landscape
    dimensions: { width: 1600, height: 900 },
    alternativeAspects: ['16:9', '1:1']
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: '📘',
    aspectRatio: '1.91:1', // Facebook link preview format
    dimensions: { width: 1200, height: 628 },
    alternativeAspects: ['1.91:1', '1:1']
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: '📸',
    aspectRatio: '4:5', // Portrait format - best for engagement
    dimensions: { width: 1080, height: 1350 },
    alternativeAspects: ['4:5', '1:1'] // Portrait and square options
  }
];

// Feed configuration (canonical format for SuperKonnected feed)
export const FEED_CONFIG = {
  aspectRatio: '4:5',
  dimensions: { width: 1080, height: 1350 }
};

// Default settings
export const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_PREVIEW_WIDTH = 2000; // Downsample for faster processing
export const JPEG_QUALITY = 0.86; // Balanced quality/compression for social uploads
export const PNG_QUALITY = 1.0;

// Aspect ratio to numeric value mapping
export const ASPECT_RATIO_VALUES: Record<AspectRatioOption, number> = {
  '1:1': 1,
  '4:5': 4 / 5,      // 0.8
  '16:9': 16 / 9,    // 1.777...
  '1.91:1': 1.91     // LinkedIn/Facebook landscape
};

// Dimension presets for alternative aspects
// Used when switching aspect ratios
export const ALTERNATIVE_DIMENSIONS: Record<AspectRatioOption, { width: number; height: number }> = {
  '1:1': { width: 1200, height: 1200 },    // Square format
  '4:5': { width: 1080, height: 1350 },    // Portrait (Instagram)
  '16:9': { width: 1600, height: 900 },    // Landscape (Twitter/X)
  '1.91:1': { width: 1200, height: 628 }   // Landscape (LinkedIn/Facebook)
};

// UI constants
export const THUMBNAIL_SIZE = 120;
export const PREVIEW_MAX_HEIGHT = 400;

// Error messages (Dutch)
export const ERROR_MESSAGES = {
  FILE_TOO_LARGE: 'Bestand is te groot (max 10MB)',
  INVALID_TYPE: 'Alleen JPEG, PNG of WebP toegestaan',
  PROCESSING_ERROR: 'Fout bij verwerken van afbeelding',
  NO_FILE: 'Geen bestand geselecteerd',
  CANVAS_ERROR: 'Canvas API niet beschikbaar',
  UNKNOWN_PLATFORM: 'Onbekend platform'
} as const;

// Get platform config by ID
export function getPlatformConfig(platformId: string): PlatformConfig | undefined {
  return PLATFORM_CONFIGS.find(p => p.id === platformId);
}

// Get all platform IDs
export function getAllPlatformIds(): string[] {
  return PLATFORM_CONFIGS.map(p => p.id);
}

// Helper to get aspect ratio value from string
export function getAspectRatioValue(aspectRatio: string): number {
  if (aspectRatio in ASPECT_RATIO_VALUES) {
    return ASPECT_RATIO_VALUES[aspectRatio as AspectRatioOption];
  }
  
  // Parse custom aspect ratio string (e.g., "16:9")
  const parts = aspectRatio.split(':');
  if (parts.length === 2) {
    const width = parseFloat(parts[0]);
    const height = parseFloat(parts[1]);
    if (!isNaN(width) && !isNaN(height) && height !== 0) {
      return width / height;
    }
  }
  
  // Default to 1:1 if parsing fails
  console.warn(`MediaEditor: Could not parse aspect ratio "${aspectRatio}", defaulting to 1:1`);
  return 1;
}
